/**
 * Lightweight address → coords geocoding via OpenStreetMap Nominatim.
 *
 * Bookings in this system rarely carry pickupLat/Lng — ops creates them with
 * address strings only. The driver-facing map needs coords to render, so we
 * geocode the address client-side on first view and cache the result in
 * localStorage forever. Subsequent views of the same trip are instant.
 *
 * Nominatim is free + CORS-friendly. The terms ask us not to flood it, which
 * we don't: each unique address is geocoded once per device (localStorage
 * cache survives reloads). For a driver who sees ~5 trips/day, that's ~5
 * requests/day per device.
 *
 * If geocoding fails (network, no result, malformed address), the caller
 * gets `null` and falls back to the no-map render path.
 */

export type GeoCoords = { lat: number; lng: number };

const CACHE_PREFIX = 'fu_geo:';
const NOMINATIM_BASE = 'https://nominatim.openstreetmap.org/search';

function cacheKey(address: string): string {
  // Normalize so trivial whitespace / case variations share a cache slot.
  return `${CACHE_PREFIX}${address.trim().toLowerCase().replace(/\s+/g, ' ')}`;
}

function readCache(address: string): GeoCoords | null {
  try {
    const raw = localStorage.getItem(cacheKey(address));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.lat === 'number' && typeof parsed?.lng === 'number') {
      return parsed;
    }
    return null;
  } catch {
    return null;
  }
}

function writeCache(address: string, coords: GeoCoords): void {
  try {
    localStorage.setItem(cacheKey(address), JSON.stringify(coords));
  } catch {
    // Quota / private-mode failure — geocode will just re-fire next time.
  }
}

export async function geocodeAddress(address: string): Promise<GeoCoords | null> {
  const trimmed = address?.trim();
  if (!trimmed) return null;

  const cached = readCache(trimmed);
  if (cached) return cached;

  const url = `${NOMINATIM_BASE}?q=${encodeURIComponent(trimmed)}&format=json&limit=1&countrycodes=in`;
  try {
    const res = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!res.ok) return null;
    const data = await res.json() as Array<{ lat: string; lon: string }>;
    const first = data?.[0];
    if (!first?.lat || !first?.lon) return null;
    const lat = parseFloat(first.lat);
    const lng = parseFloat(first.lon);
    if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
    const coords: GeoCoords = { lat, lng };
    writeCache(trimmed, coords);
    return coords;
  } catch {
    return null;
  }
}
